import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../constants';
import { Button } from '@/components/ui/button';
import { ShieldAlert, LogOut, Eye, PenSquare } from 'lucide-react';

/**
 * Persistent red top banner shown while the staff member is impersonating
 * a client account. Sits at z-index 100 so it covers any tab content. Includes
 * a live countdown to expiry + one-click Exit.
 */
export default function ImpersonationBanner() {
  const { impersonation, endImpersonation } = useAuth();
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!impersonation) return undefined;
    const tick = () => {
      const ms = new Date(impersonation.expires_at).getTime() - Date.now();
      if (ms <= 0) {
        toast.info('Impersonation session expired — exiting');
        handleExit('expired');
        return;
      }
      const m = Math.floor(ms / 60000);
      const s = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
      setRemaining(`${m}:${s}`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonation]);

  if (!impersonation) return null;

  const handleExit = async (cause = 'user') => {
    try {
      if (cause !== 'expired') {
        await axios.post(`${API}/admin/developers/impersonate/end`, {
          session_id: impersonation.session_id,
        });
      }
    } catch { /* still restore the local token */ }
    endImpersonation();
    toast.success('Exited impersonation — back to your admin session');
    navigate('/admin');
  };

  const modeMeta = impersonation.mode === 'write'
    ? { label: 'WRITE MODE', Icon: PenSquare, bg: 'bg-red-600', sub: 'Mutations are LIVE — every action is double-logged.' }
    : { label: 'READ-ONLY', Icon: Eye, bg: 'bg-amber-500', sub: 'Mutations are blocked. View-only.' };

  return (
    <div
      className={`sticky top-0 z-[100] ${modeMeta.bg} text-white shadow-md`}
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
        <ShieldAlert className="h-5 w-5 shrink-0" />
        <modeMeta.Icon className="h-4 w-4 shrink-0 opacity-90" />
        <div className="flex-1 min-w-0 text-sm leading-tight">
          <div className="font-semibold">
            Acting as <span className="font-mono bg-black/20 px-1.5 py-0.5 rounded" data-testid="impersonation-target-email">{impersonation.target_email}</span>
            <span className="ml-2 inline-block text-[10px] font-bold tracking-wide bg-black/30 px-1.5 py-0.5 rounded" data-testid="impersonation-mode-pill">{modeMeta.label}</span>
          </div>
          <div className="text-[11px] opacity-90 truncate">
            {modeMeta.sub} · Session ends in <span data-testid="impersonation-countdown">{remaining}</span> · Reason: {impersonation.reason}
          </div>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => handleExit('user')}
          className="bg-white text-red-700 hover:bg-white/90 font-semibold shrink-0"
          data-testid="exit-impersonation-btn"
        >
          <LogOut className="h-4 w-4 mr-1.5" /> Exit
        </Button>
      </div>
    </div>
  );
}
